import { useState, useMemo } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  Download,
  RefreshCw,
  Loader2,
  Bell,
  TrendingUp,
  TrendingDown,
  ChefHat,
  Timer,
  Utensils,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  useAlertHistory,
  useAlertRules,
  getRuleTypeLabel,
  getSeverityColor,
  type AlertSeverity,
} from '@/hooks/useAlerts';
import { useLocations } from '@/hooks/useLocations';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

const SEVERITY_ICONS: Record<AlertSeverity, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertCircle,
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-red-100 text-red-800', icon: Bell },
  acknowledged: { label: 'Acknowledged', color: 'bg-amber-100 text-amber-800', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  dismissed: { label: 'Dismissed', color: 'bg-gray-100 text-gray-800', icon: XCircle },
};

// Kitchen order threshold in minutes (configurable)
const KITCHEN_SLA_THRESHOLD_MINUTES = 15;

interface KitchenOrder {
  id: string;
  order_id: string;
  station_id: string | null;
  status: string | null;
  sent_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  items: any;
  station?: { id: string; name: string } | null;
  order?: { order_number: string; table_number?: string | null } | null;
}

export default function SLAReports() {
  const [activeTab, setActiveTab] = useState<'alerts' | 'kitchen'>('alerts');
  const [filters, setFilters] = useState({
    status: '',
    severity: '',
    startDate: '',
    endDate: '',
    locationId: '',
  });
  const [kitchenThreshold, setKitchenThreshold] = useState(KITCHEN_SLA_THRESHOLD_MINUTES);

  const { currentLocation } = useLocationStore();

  const { data: alertHistory = [], isLoading, refetch } = useAlertHistory({
    status: filters.status || undefined,
    severity: filters.severity || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    limit: 100,
  });

  const { data: rules = [] } = useAlertRules();
  const { locations } = useLocations();

  // Fetch kitchen orders for performance tracking
  const { data: kitchenOrders = [], isLoading: isKitchenLoading, refetch: refetchKitchen } = useQuery({
    queryKey: ['kitchen-orders-sla', currentLocation?.id, filters.startDate, filters.endDate],
    queryFn: async () => {
      let query = supabase
        .from('kitchen_orders')
        .select(`
          id,
          order_id,
          station_id,
          status,
          sent_at,
          started_at,
          completed_at,
          items,
          station:kitchen_stations(id, name),
          order:orders(order_number, table_number)
        `)
        .order('sent_at', { ascending: false })
        .limit(500);

      // Apply date filters
      if (filters.startDate) {
        query = query.gte('sent_at', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('sent_at', `${filters.endDate}T23:59:59`);
      }

      const { data, error } = await query;
      if (error) throw error;
      // Transform the data to match KitchenOrder interface
      return (data || []).map((item: any) => ({
        ...item,
        station: Array.isArray(item.station) ? item.station[0] : item.station,
        order: Array.isArray(item.order) ? item.order[0] : item.order,
      })) as KitchenOrder[];
    },
    enabled: activeTab === 'kitchen',
  });

  // Calculate kitchen performance metrics
  const kitchenStats = useMemo(() => {
    if (!kitchenOrders.length) {
      return {
        total: 0,
        completed: 0,
        overThreshold: 0,
        avgPrepTime: 0,
        byStation: [] as { name: string; total: number; overThreshold: number; avgTime: number }[],
        byHour: [] as { hour: string; total: number; overThreshold: number }[],
        slaBreaches: [] as KitchenOrder[],
      };
    }

    const thresholdMs = kitchenThreshold * 60 * 1000;
    const completedOrders = kitchenOrders.filter(o => o.completed_at && o.sent_at);
    
    // Orders that exceeded threshold
    const overThresholdOrders = completedOrders.filter(order => {
      const prepTime = new Date(order.completed_at!).getTime() - new Date(order.sent_at!).getTime();
      return prepTime > thresholdMs;
    });

    // Average prep time
    const totalPrepTime = completedOrders.reduce((sum, order) => {
      const prepTime = new Date(order.completed_at!).getTime() - new Date(order.sent_at!).getTime();
      return sum + prepTime;
    }, 0);
    const avgPrepTime = completedOrders.length > 0 ? totalPrepTime / completedOrders.length / 60000 : 0;

    // Group by station
    const stationMap = new Map<string, { total: number; overThreshold: number; totalTime: number }>();
    completedOrders.forEach(order => {
      const stationName = order.station?.name || 'Unknown Station';
      const prepTime = new Date(order.completed_at!).getTime() - new Date(order.sent_at!).getTime();
      const isOver = prepTime > thresholdMs;

      if (!stationMap.has(stationName)) {
        stationMap.set(stationName, { total: 0, overThreshold: 0, totalTime: 0 });
      }
      const station = stationMap.get(stationName)!;
      station.total++;
      station.totalTime += prepTime;
      if (isOver) station.overThreshold++;
    });

    const byStation = Array.from(stationMap.entries()).map(([name, data]) => ({
      name,
      total: data.total,
      overThreshold: data.overThreshold,
      avgTime: data.total > 0 ? data.totalTime / data.total / 60000 : 0,
    })).sort((a, b) => b.overThreshold - a.overThreshold);

    // Group by hour
    const hourMap = new Map<number, { total: number; overThreshold: number }>();
    completedOrders.forEach(order => {
      const hour = new Date(order.sent_at!).getHours();
      const prepTime = new Date(order.completed_at!).getTime() - new Date(order.sent_at!).getTime();
      const isOver = prepTime > thresholdMs;

      if (!hourMap.has(hour)) {
        hourMap.set(hour, { total: 0, overThreshold: 0 });
      }
      const hourData = hourMap.get(hour)!;
      hourData.total++;
      if (isOver) hourData.overThreshold++;
    });

    const byHour = Array.from(hourMap.entries())
      .map(([hour, data]) => ({
        hour: `${hour.toString().padStart(2, '0')}:00`,
        total: data.total,
        overThreshold: data.overThreshold,
      }))
      .sort((a, b) => parseInt(a.hour) - parseInt(b.hour));

    // Recent SLA breaches (with details)
    const slaBreaches = kitchenOrders
      .filter(order => {
        if (!order.sent_at) return false;
        const endTime = order.completed_at ? new Date(order.completed_at).getTime() : Date.now();
        const prepTime = endTime - new Date(order.sent_at).getTime();
        return prepTime > thresholdMs;
      })
      .slice(0, 20);

    return {
      total: kitchenOrders.length,
      completed: completedOrders.length,
      overThreshold: overThresholdOrders.length,
      avgPrepTime,
      byStation,
      byHour,
      slaBreaches,
    };
  }, [kitchenOrders, kitchenThreshold]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const total = alertHistory.length;
    const byStatus = {
      active: alertHistory.filter(a => a.status === 'active').length,
      acknowledged: alertHistory.filter(a => a.status === 'acknowledged').length,
      resolved: alertHistory.filter(a => a.status === 'resolved').length,
      dismissed: alertHistory.filter(a => a.status === 'dismissed').length,
    };
    const bySeverity = {
      critical: alertHistory.filter(a => a.severity === 'critical').length,
      warning: alertHistory.filter(a => a.severity === 'warning').length,
      info: alertHistory.filter(a => a.severity === 'info').length,
    };
    
    // Calculate average resolution time (for resolved alerts)
    const resolvedAlerts = alertHistory.filter(a => a.status === 'resolved' && a.resolved_at);
    const avgResolutionMinutes = resolvedAlerts.length > 0
      ? resolvedAlerts.reduce((sum, a) => {
          const created = new Date(a.created_at).getTime();
          const resolved = new Date(a.resolved_at!).getTime();
          return sum + (resolved - created) / 60000;
        }, 0) / resolvedAlerts.length
      : 0;

    // Group by rule type
    const byRuleType = alertHistory.reduce((acc, alert) => {
      const type = alert.rule_type;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Group by day for chart
    const byDay = alertHistory.reduce((acc, alert) => {
      const date = new Date(alert.created_at).toLocaleDateString('en-US', { weekday: 'short' });
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total,
      byStatus,
      bySeverity,
      avgResolutionMinutes,
      byRuleType,
      byDay,
      resolutionRate: total > 0 ? (byStatus.resolved / total) * 100 : 0,
    };
  }, [alertHistory]);

  // Prepare chart data
  const statusPieData = [
    { name: 'Active', value: stats.byStatus.active, color: '#ef4444' },
    { name: 'Acknowledged', value: stats.byStatus.acknowledged, color: '#f59e0b' },
    { name: 'Resolved', value: stats.byStatus.resolved, color: '#22c55e' },
    { name: 'Dismissed', value: stats.byStatus.dismissed, color: '#94a3b8' },
  ].filter(d => d.value > 0);

  const ruleTypeBarData = Object.entries(stats.byRuleType).map(([type, count]) => ({
    name: getRuleTypeLabel(type as any),
    count,
  }));

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-semibold">SLA & Alert Reports</h2>
          <p className="text-sm text-muted-foreground">
            Monitor alert performance, resolution times, and kitchen SLA compliance
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            refetch();
            refetchKitchen();
          }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Tabs for Alerts vs Kitchen */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'alerts' | 'kitchen')}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="alerts" className="gap-2">
            <Bell className="h-4 w-4" />
            Alert Performance
          </TabsTrigger>
          <TabsTrigger value="kitchen" className="gap-2">
            <ChefHat className="h-4 w-4" />
            Kitchen Performance
          </TabsTrigger>
        </TabsList>

        {/* Alert Performance Tab */}
        <TabsContent value="alerts" className="space-y-6 mt-6">
          {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Alerts</span>
              <Bell className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">{stats.total}</p>
            <div className="flex gap-2 mt-2">
              <Badge variant="destructive" className="text-xs">
                {stats.byStatus.active} active
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Critical Alerts</span>
              <AlertCircle className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-3xl font-bold text-red-600">{stats.bySeverity.critical}</p>
            <p className="text-xs text-muted-foreground mt-2">
              {stats.bySeverity.warning} warnings, {stats.bySeverity.info} info
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Resolution Rate</span>
              {stats.resolutionRate >= 80 ? (
                <TrendingUp className="h-4 w-4 text-green-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-amber-500" />
              )}
            </div>
            <p className="text-3xl font-bold">
              {stats.resolutionRate.toFixed(0)}%
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {stats.byStatus.resolved} of {stats.total} resolved
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Avg Resolution Time</span>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">
              {formatDuration(stats.avgResolutionMinutes)}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Based on {stats.byStatus.resolved} resolved alerts
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Alert Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {statusPieData.length > 0 ? (
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {statusPieData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alerts by Rule Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Alerts by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {ruleTypeBarData.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ruleTypeBarData} layout="vertical">
                    <XAxis type="number" />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={120}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="dismissed">Dismissed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={filters.severity}
                onValueChange={(v) => setFilters({ ...filters, severity: v === 'all' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All severities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Location</Label>
              <Select
                value={filters.locationId}
                onValueChange={(v) => setFilters({ ...filters, locationId: v === 'all' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alert History Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Alert History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : alertHistory.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No alerts found matching your filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Resolution Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alertHistory.map((alert) => {
                  const SeverityIcon = SEVERITY_ICONS[alert.severity];
                  const statusConfig = STATUS_CONFIG[alert.status as keyof typeof STATUS_CONFIG];
                  const resolutionTime = alert.resolved_at
                    ? (new Date(alert.resolved_at).getTime() - new Date(alert.created_at).getTime()) / 60000
                    : null;

                  return (
                    <TableRow key={alert.id}>
                      <TableCell className="text-sm">
                        {formatDate(alert.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <SeverityIcon className={cn(
                            'h-4 w-4',
                            alert.severity === 'critical' && 'text-red-500',
                            alert.severity === 'warning' && 'text-amber-500',
                            alert.severity === 'info' && 'text-blue-500'
                          )} />
                          <Badge className={getSeverityColor(alert.severity)}>
                            {alert.severity}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{alert.title}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[300px]">
                            {alert.message}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {getRuleTypeLabel(alert.rule_type)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {alert.location?.name || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {resolutionTime !== null ? (
                          formatDuration(resolutionTime)
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

          {/* Active Rules Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Active Alert Rules</CardTitle>
            </CardHeader>
            <CardContent>
              {rules.filter(r => r.is_active).length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  No active alert rules configured
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rules.filter(r => r.is_active).map((rule) => (
                    <div
                      key={rule.id}
                      className="p-4 rounded-lg border bg-card"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{rule.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {getRuleTypeLabel(rule.rule_type)}
                          </p>
                        </div>
                        <Badge className={getSeverityColor(rule.severity)}>
                          {rule.severity}
                        </Badge>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Badge variant="outline" className="text-xs">
                          {rule.location?.name || 'All Locations'}
                        </Badge>
                        {rule.send_email && (
                          <Badge variant="outline" className="text-xs">Email</Badge>
                        )}
                        {rule.send_in_app && (
                          <Badge variant="outline" className="text-xs">In-App</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Kitchen Performance Tab */}
        <TabsContent value="kitchen" className="space-y-6 mt-6">
          {/* Kitchen Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Timer className="h-4 w-4" />
                SLA Threshold
              </CardTitle>
              <CardDescription>
                Set the maximum acceptable preparation time for kitchen orders
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="120"
                    value={kitchenThreshold}
                    onChange={(e) => setKitchenThreshold(parseInt(e.target.value) || 15)}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">minutes</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Orders taking longer than this will be flagged as SLA breaches
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Kitchen Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Total Orders</span>
                  <Utensils className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-3xl font-bold">{kitchenStats.total}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {kitchenStats.completed} completed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">SLA Breaches</span>
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                </div>
                <p className="text-3xl font-bold text-amber-600">{kitchenStats.overThreshold}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Over {kitchenThreshold} min threshold
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">SLA Compliance</span>
                  {kitchenStats.completed > 0 && (kitchenStats.completed - kitchenStats.overThreshold) / kitchenStats.completed >= 0.9 ? (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-amber-500" />
                  )}
                </div>
                <p className="text-3xl font-bold">
                  {kitchenStats.completed > 0
                    ? (((kitchenStats.completed - kitchenStats.overThreshold) / kitchenStats.completed) * 100).toFixed(0)
                    : 100}%
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Orders within SLA
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Avg Prep Time</span>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-3xl font-bold">
                  {formatDuration(kitchenStats.avgPrepTime)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Target: {kitchenThreshold} min
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Kitchen Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance by Station */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Performance by Station</CardTitle>
              </CardHeader>
              <CardContent>
                {kitchenStats.byStation.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={kitchenStats.byStation} layout="vertical">
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value: number, name: string) => [
                            name === 'overThreshold' ? `${value} breaches` : `${value} total`,
                            name === 'overThreshold' ? 'SLA Breaches' : 'Total Orders'
                          ]}
                        />
                        <Legend />
                        <Bar dataKey="total" fill="#3b82f6" name="Total Orders" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="overThreshold" fill="#f59e0b" name="SLA Breaches" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <ChefHat className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>No kitchen order data available</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Orders by Hour */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Orders by Hour</CardTitle>
              </CardHeader>
              <CardContent>
                {kitchenStats.byHour.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={kitchenStats.byHour}>
                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="total" 
                          stroke="#3b82f6" 
                          name="Total Orders"
                          strokeWidth={2}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="overThreshold" 
                          stroke="#f59e0b" 
                          name="SLA Breaches"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Clock className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>No hourly data available</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Station Average Times */}
          {kitchenStats.byStation.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Station Performance Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Station</TableHead>
                      <TableHead className="text-right">Total Orders</TableHead>
                      <TableHead className="text-right">SLA Breaches</TableHead>
                      <TableHead className="text-right">Breach Rate</TableHead>
                      <TableHead className="text-right">Avg Prep Time</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kitchenStats.byStation.map((station) => {
                      const breachRate = station.total > 0 ? (station.overThreshold / station.total) * 100 : 0;
                      return (
                        <TableRow key={station.name}>
                          <TableCell className="font-medium">{station.name}</TableCell>
                          <TableCell className="text-right">{station.total}</TableCell>
                          <TableCell className="text-right">
                            <span className={station.overThreshold > 0 ? 'text-amber-600 font-medium' : ''}>
                              {station.overThreshold}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={breachRate > 20 ? 'text-red-600' : breachRate > 10 ? 'text-amber-600' : 'text-green-600'}>
                              {breachRate.toFixed(1)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={station.avgTime > kitchenThreshold ? 'text-amber-600' : ''}>
                              {formatDuration(station.avgTime)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {breachRate <= 10 ? (
                              <Badge className="bg-green-100 text-green-800">Good</Badge>
                            ) : breachRate <= 20 ? (
                              <Badge className="bg-amber-100 text-amber-800">Warning</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800">Critical</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* SLA Breach Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent SLA Breaches</CardTitle>
              <CardDescription>
                Orders that exceeded the {kitchenThreshold}-minute threshold
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isKitchenLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : kitchenStats.slaBreaches.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
                  <p className="font-medium">No SLA breaches found</p>
                  <p className="text-sm">All orders are within the {kitchenThreshold}-minute threshold</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time Sent</TableHead>
                      <TableHead>Order #</TableHead>
                      <TableHead>Table</TableHead>
                      <TableHead>Station</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Prep Time</TableHead>
                      <TableHead className="text-right">Over By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kitchenStats.slaBreaches.map((order) => {
                      const endTime = order.completed_at ? new Date(order.completed_at).getTime() : Date.now();
                      const prepTimeMs = order.sent_at ? endTime - new Date(order.sent_at).getTime() : 0;
                      const prepTimeMin = prepTimeMs / 60000;
                      const overBy = prepTimeMin - kitchenThreshold;

                      return (
                        <TableRow key={order.id}>
                          <TableCell className="text-sm">
                            {order.sent_at ? formatDate(order.sent_at) : '-'}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {order.order?.order_number || '-'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {order.order?.table_number || '-'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {order.station?.name || 'Unknown'}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={order.status === 'completed' ? 'default' : 'secondary'}
                              className={order.status === 'completed' ? 'bg-green-100 text-green-800' : ''}
                            >
                              {order.status || 'pending'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium text-amber-600">
                            {formatDuration(prepTimeMin)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            +{formatDuration(overBy)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

